import { Edit2, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { POSITION_COLORS } from '../constants/positionColors'

const GOLD = '#4F8EF7'

const RESULT_COLOR = { W: '#22c55e', N: '#eab308', L: '#ef4444' }
const FORM_EMPTY = 'rgba(26,25,22,0.08)'

function calcAge(birthDate) {
  if (!birthDate) return null
  const b = new Date(birthDate)
  if (Number.isNaN(b.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - b.getFullYear()
  const m = now.getMonth() - b.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--
  return age
}

function PlayerCard({ player, form = [], onEdit, onDelete }) {
  const navigate = useNavigate()

  const mj   = player.matches_played ?? 0
  const min  = player.total_minutes ?? 0
  const buts = player.goals ?? 0
  const regular = mj >= 5

  // Bordure selon volume de matchs — lisible sur fond page clair.
  let borderColor
  if (regular)     borderColor = 'rgba(79,142,247,0.35)'
  else if (mj > 0) borderColor = 'rgba(26,25,22,0.12)'
  else             borderColor = 'rgba(26,25,22,0.07)'

  const numColor = regular ? GOLD : (mj > 0 ? 'rgba(245,242,235,0.5)' : 'rgba(245,242,235,0.2)')

  const posC = POSITION_COLORS[player.position] || {
    color: 'rgba(245,242,235,0.7)', bg: 'transparent', abbr: (player.position || '').slice(0, 3).toUpperCase(),
  }
  const abbr = posC.abbr

  const isActive = (player.status ?? 'actif') === 'actif'
  const age = calcAge(player.birth_date)
  const subParts = []
  if (player.preferred_foot) subParts.push(`Pied ${player.preferred_foot}`)
  if (age != null) subParts.push(`${age} ans`)
  const subtitle = subParts.join(' · ')

  const statCell = (value, label, color, extra = {}) => (
    <div style={{ ...extra }}>
      <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', color: 'rgba(26,25,22,0.4)', marginTop: 3 }}>{label}</div>
    </div>
  )

  return (
    <div
      onClick={() => navigate(`/dashboard/player/${player.id}`)}
      className="pcard-root"
      style={{
        border: `1px solid ${borderColor}`,
        borderRadius: 12, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        cursor: 'pointer', position: 'relative',
      }}
    >
      {/* Actions hover */}
      <div className="pcard-actions" style={{
        position: 'absolute', top: 8, right: 8, zIndex: 2,
        display: 'flex', gap: 4, opacity: 0, transition: 'opacity .15s',
      }}>
        {onEdit && (
          <button onClick={e => { e.stopPropagation(); onEdit(player) }} aria-label="Modifier"
            style={{ background: 'rgba(245,242,235,0.06)', border: '1px solid rgba(79,142,247,0.2)', padding: '3px 5px', cursor: 'pointer', display: 'flex' }}>
            <Edit2 size={12} color={GOLD} />
          </button>
        )}
        {onDelete && (
          <button onClick={e => { e.stopPropagation(); onDelete(player) }} aria-label="Supprimer"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', padding: '3px 5px', cursor: 'pointer', display: 'flex' }}>
            <Trash2 size={12} color="#ef4444" />
          </button>
        )}
      </div>

      {/* Header dark : numéro + tag poste + statut */}
      <div style={{
        background: '#0d0c0a',
        padding: '12px 14px 10px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <span style={{ fontFamily: 'monospace', fontSize: 32, fontWeight: 900, lineHeight: 1, color: numColor }}>
          {player.number ?? '--'}
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
          <span style={{
            fontFamily: 'monospace', fontSize: 8, fontWeight: 700, letterSpacing: '.12em',
            padding: '2px 6px', color: posC.color, background: posC.bg,
            border: `1px solid ${posC.color}40`,
          }}>
            {abbr}
          </span>
          <span style={{
            fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em',
            color: isActive ? '#22c55e' : 'rgba(245,242,235,0.4)',
          }}>
            {isActive ? '● ACTIF' : '○ INACTIF'}
          </span>
        </div>
      </div>

      {/* Body light : nom, sous-titre, stats, forme */}
      <div style={{ background: '#ffffff', padding: '10px 14px 14px', flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'rgba(26,25,22,0.92)', lineHeight: 1.2, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {player.name}
        </div>
        <div style={{ fontSize: 9, color: 'rgba(26,25,22,0.45)', marginBottom: 14 }}>
          {subtitle || '—'}
        </div>

        {mj > 0 ? (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            borderTop: '1px solid rgba(26,25,22,0.08)', paddingTop: 10,
          }}>
            {statCell(mj, 'MJ', 'rgba(26,25,22,0.85)')}
            {statCell(min, 'MIN', 'rgba(26,25,22,0.85)', {
              borderLeft: '1px solid rgba(26,25,22,0.08)',
              borderRight: '1px solid rgba(26,25,22,0.08)',
              paddingLeft: 8,
            })}
            {statCell(buts, 'BUTS', buts > 0 ? GOLD : 'rgba(26,25,22,0.3)', { paddingLeft: 8 })}
          </div>
        ) : (
          <div style={{ borderTop: '1px solid rgba(26,25,22,0.08)', paddingTop: 10 }}>
            <span style={{ fontStyle: 'italic', fontSize: 9, color: 'rgba(26,25,22,0.4)', letterSpacing: '.06em' }}>
              Aucun match cette saison
            </span>
          </div>
        )}

        {mj > 0 && (
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.12em', color: 'rgba(26,25,22,0.4)' }}>FORME</span>
            <div style={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} style={{ width: 6, height: 10, background: form[i] ? RESULT_COLOR[form[i]] : FORM_EMPTY }} />
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`.pcard-root:hover .pcard-actions { opacity: 1 !important; }`}</style>
    </div>
  )
}

export default PlayerCard
